import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { useFieldArray } from 'react-final-form-arrays';

import { AddOutlined } from '@mui/icons-material';
import { Box, Fade, IconButton, SxProps } from '@mui/material';
import { useSetCurrentBlockId } from '@wp-block-builder/editor/hooks';
import { TBlock } from '@wp-block-builder/package';
import { useOpenAddBlockMenu } from '@wp-block-builder/stores/AddBlockMenuStore';

const WRAPPER_SX: SxProps = { position: 'relative' };
const ICON_BUTTON_SX: SxProps = {
  p: 0.12,
  position: 'absolute',
  top: '-12px',
  left: '50%',
  transform: 'translateX(-10px)',
  bgcolor: 'brand.blue',
  color: 'primary.contrastText',
  zIndex: 'fab',
  '&:hover, &:active, &:focus': {
    bgcolor: 'brand.blue',
    color: 'primary.contrastText',
  },
};

type Props = {
  blocksArrayName: string;
  index?: number;
};
export default function AddContainerBlockButton({ blocksArrayName, index }: Props) {
  const { fields } = useFieldArray<TBlock>(blocksArrayName);
  const [buttonElement, setButtonElement] = useState<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const open = useOpenAddBlockMenu();
  const setCurrentBlockId = useSetCurrentBlockId();
  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      open(ev.currentTarget, (block) => {
        if (index === undefined) {
          fields.push(block);
        } else {
          fields.insert(index, block);
        }
        setCurrentBlockId(block.id);
      });
    },
    [fields, open, setCurrentBlockId, index]
  );

  useEffect(() => {
    function listener({ clientX, clientY }: MouseEvent) {
      if (!buttonElement) {
        return;
      }
      const rect = buttonElement.getBoundingClientRect();
      const rectY = rect.y;
      const bottomX = rect.x;
      const topX = bottomX + rect.width;

      if (Math.abs(clientY - rectY) < 20) {
        if (bottomX < clientX && clientX < topX) {
          setVisible(true);
          return;
        }
      }
      setVisible(false);
    }
    window.addEventListener('mousemove', listener);
    return () => {
      window.removeEventListener('mousemove', listener);
    };
  }, [buttonElement, setVisible]);

  return (
    <Box ref={setButtonElement} sx={WRAPPER_SX}>
      <Fade in={visible}>
        <IconButton onClick={onClick} size="small" sx={ICON_BUTTON_SX}>
          <AddOutlined fontSize="small" />
        </IconButton>
      </Fade>
    </Box>
  );
}
